// src/Api/apiService.js
import { Api, PublicApi } from "Api/axiosInstance";
import axios from "axios";
import { API_ENDPOINTS } from "config/ApiConfig";
import { TABLE_FILTER_CONSTANTS } from "constants/employee.constants";

// ── Ask Allie SSE client ──────────────────────────────────────────────────────
/**
 * Open a streaming SSE connection to /api/allie/chat.
 * Returns an object with { abort } to cancel the request.
 *
 * @param {{ message, pageContext, wizardStep, userRole, sessionId, conversationHistory }} payload
 * @param {{ onToken, onDone, onError }} callbacks
 *   onDone receives { sessionId, messageId } from the server's done event.
 */
export const fetchAllieHistory = async () => {
  const response = await Api.get(API_ENDPOINTS.ALLIE_SESSIONS);
  return response.data;
};

export const callAllieBackend = (payload, callbacks) => {
  const { onToken, onDone, onError } = callbacks;
  const source = axios.CancelToken.source();

  const run = async () => {
    try {
      const { data } = await Api.post(API_ENDPOINTS.ALLIE_CHAT, payload, {
        cancelToken: source.token,
      });

      if (data?.text) onToken?.(data.text);
      onDone?.({ sessionId: data?.sessionId, messageId: data?.messageId });
    } catch (err) {
      if (axios.isCancel(err)) return;
      const errData = err.response?.data;
      onError?.(
        errData?.error || err.message || `Request failed (${err.response?.status ?? ""})`
      );
    }
  };

  run();
  return { abort: () => source.cancel() };
};


// Authentication - SignIn
export const loginUser = async (username, password, captcha_token) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.AUTHENICATION, {
      username,
      password,
      captcha_token,
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - SignUp
export const registerTempUser = async (first_name, last_name, email, company_name, title) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.REGISTER_TEMP_USER, {
      first_name,
      last_name,
      email,
      company_name,
      title
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - verifyOtpWithInvitationID
export const verifyOtpWithInvitationID = async (invitationId, otp) => {
  try {
    const url = `${API_ENDPOINTS.VERIFY_OTP_SIGNUP}/${invitationId}/validate/otp`;
    const response = await PublicApi.post(url, {
      otp
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};


// Authentication - reSendOtpforSignup
export const reSendOtpSignUp = async (invitationId) => {
  try {
    const url = `${API_ENDPOINTS.RESEND_OTP_SIGNUP}/${invitationId}/resend/otp`;
    const response = await PublicApi.post(url);
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - createNewUser
export const createNewUser = async (otp_verification_code, password) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.CREATE_NEW_USER, {
      otp_verification_code,
      password
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - forgotPassword
export const forgotPassword = async (email, captcha_token) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.FORGOT_PASSWORD, {
      email,
      captcha_token,
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - verifyOtpWithEMail
export const verifyOtpWithEMail = async (email, otp) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.VERIFY_OTP_FORGOT, {
      email,
      otp
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};


// Authentication - resetPassword
export const resetPassword = async (email, otp_verification_code, password) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.SET_PASSWORD, {
      email,
      otp_verification_code,
      password,
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};

// Authentication - reSendOtpForgotPassword
export const reSendOtpForgotPassword = async (email) => {
  try {
    const response = await PublicApi.post(API_ENDPOINTS.RESEND_OTP_FORGOT, {
      email,
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw the error to be handled by the calling component
  }
};


// function to re authorise token 

export const reAuthoriseToken = async (refreshToken) => {
  try {
    const response = await axios.post(API_ENDPOINTS.RE_AUTHORISE_TOKEN, undefined, {
      headers: {
        Authorization: `Bearer ${refreshToken}`
      },
    });
    return response.data;
  } catch (error) {
    throw error; // Rethrow the error to be handled in the calling component
  }
};


//api/role/entitlements

export const getEntitlement = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ENTITLEMENT);
    return response.data;
  } catch (error) {
    throw error; // Rethrow the error to be handled in the calling component
  }
};


//fetching roles for jobs library page
export const fetchRoles = async (email) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.FETCH_ROLES}?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error; // Rethrow the error to be handled in the calling component
  }
};

// updateCriticalRole
export const updateCriticalRole = async (payload) => {
  try {
    const response = await Api.post(`${API_ENDPOINTS.UPDATE_CRITICAL_ROLE}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating priority role:', error);
    throw error;
  }
};


// Logout function using axios
export const logoutUser = async (accessToken, refreshToken) => {
  try {
    const response = await axios.delete(API_ENDPOINTS.LOGOUT, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Refresh-Token": refreshToken,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
    } else {
      throw new Error("Logout failed");
    }
  } catch (error) {
    throw error; // Rethrow the error to be handled in the calling component
  }
};

export const getJobView = async (email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.JOBS_VIEW}?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

export const getRnCJobView = async (email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.RNC_JOBS_VIEW}?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

export const getJobRequisitionView = async (email, viewAll = false) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.REQUISITION_VIEW}?email=${encodeURIComponent(email)}${viewAll ? "&view_all=true" : ""}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

export const getRnCJobRequisitionView = async (email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.RNC_REQUISITION_VIEW}?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};


// Protected Data (Authentication)
export const getProtectedData = async () => {
  try {
    const response = await Api.get("protected");
    return response.data;
  } catch (error) {
    console.error("Error fetching protected data:", error);
    throw error;
  }
};

// Fetch Interview Questions
export const getInterviewQuestions = async (
  role,
  jd_text,
  email,
  sb_unique_id
) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.INTERVIEW_QUESTIONS,
      {
        question_owner: role,
        jd_text,
        email,
        sb_unique_id,
      }
    );
    return response.data; // Return the data from the API
  } catch (error) {
    console.error("Error fetching interview questions:", error);
    throw error; // Throw error for the component to handle
  }
};

// Save Comment for a Question
export const saveComment = async (
  questionId,
  comment,
  sb_unique_id,
  email,
  role
) => {
  try {
    const response = await Api.post(API_ENDPOINTS.UPDATE_COMMENT, {
      id: questionId,
      comment,
      unique_id: sb_unique_id,
      email,
      role,
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error saving comment:", error);
    throw error; // Throw error for the component to handle
  }
};

//this is to fetch versions
export const getVersions = async (email, job_id) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.VERSIONS}?email=${encodeURIComponent(
        email
      )}&job_id=${job_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching versions:", error);
    throw error;
  }
};

export const getRnCVersions = async (email, job_id) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.RNC_VERSIONS}?email=${encodeURIComponent(
        email
      )}&job_id=${job_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching versions:", error);
    throw error;
  }
};

// Fetch Error Message
export const getErrorMessage = async (jobId) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ERROR_MESSAGE}?job_id=${jobId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching error message:", error);
    throw error;
  }
};

// Fetch Job Description (Original and Revised)
export const getOGJobDescription = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ORIGINAL_JOB_DESCRIPTION}?email=${email}&job_id=${jobId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching og job description:", error);
    throw error;
  }
};

// Fetch Job Description (Original and Revised)
export const getSBJobDescription = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.SB_JOB_DESCRIPTION}?email=${email}&sb_unique_id=${jobId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sb job description:", error);
    throw error;
  }
};

// Fetch Automation Potential
export const getAutomationPotential = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.AUTOMATION_POTENTIAL}?job_id=${jobId}&email=${email}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching automation potential:", error);
    throw error;
  }
};

export const getCompareData = async (jobIdR, jobIdL, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.COMPARE_JOBS}?sb_unique_id_r=${jobIdR}&sb_unique_id_l=${jobIdL}&email=${email}&identifier_type=sb_unique_id`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching compare data:", error);
    throw error;
  }
};

export const createVersion = async (requestBody) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.CREATE_VERSION,
      requestBody,
    );

    return response.data;
  } catch (error) {
    console.error("Error generating version:", error);
    throw error;
  }
};


export const safetyCheck = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.SAFETYCHECK, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error validating safety check:", error);
    throw error; // Throw error to handle it in the component
  }
};

export const uploadJD = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.UPLOAD, formData);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error enhancing JD:", error);
    throw error; // Throw error to handle it in the component
  }
};

export const uploadJDInsert = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.UPLOAD_INSERT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error enhancing JD:", error);
    throw error; // Throw error to handle it in the component
  }
};

export const createReviseJDProgress = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.CREATE_REVISE_JD_PROGRESS, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error enhancing JD:", error);
    throw error;
  }
};

export const createJD = async (formData) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.CREATE_JD,
      formData
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error creating JD:", error);
    throw error; // Throw error to handle it in the component
  }
};

export const createJDProgress = async (formData) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.CREATE_JD_PROGRESS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating JD Progress", error);
    throw error;
  }
};


export const createJdInsert = async (formData) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.CREATE_JD_INSERT,
      formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error creating JD:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Fetch original_jd_eval_score
export const getOgjdEvalScore = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.FETCH_OG_JD_EVAL}?job_id=${jobId}&email=${email}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching og jd eval score:", error);
    throw error;
  }
};


// Fetch sb_jd_eval_score
export const getSbjdEvalScore = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.FETCH_SB_JD_EVAL}?job_id=${jobId}&email=${email}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sb jd eval score:", error);
    throw error;
  }
};


// Fetch fetch_org_metaData
export const fetchOrgMetaData = async () => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.FETCH_ORG_METADATA}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Meta Data:", error);
    throw error;
  }
};

export const fetchOrgDomains = async (orgId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.FETCH_ORG_DOMAINS(orgId));
    return response.data;
  } catch (error) {
    console.error("Error fetching organization domains:", error);
    throw error;
  }
};

// GET organization context for admin setup
export const getOrgContextAdminSetup = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_CONTEXT_ADMIN_SETUP);
    return response;
  } catch (error) {
    console.error("Error fetching organization context:", error);
    throw error;
  }
};

// PATCH organization context for admin setup
export const updateOrgContextAdminSetup = async (payload) => {
  try {
    const response = await Api.patch(API_ENDPOINTS.ORG_CONTEXT_ADMIN_SETUP, payload);
    return response;
  } catch (error) {
    console.error("Error updating organization context:", error);
    throw error;
  }
};

// Fetch fetch_org_structure
export const fetchOrgStructure = async () => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.FETCH_ORG_STRUCTURE}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Org Structure:", error);
    throw error;
  }
};



// Fetch fetch_error_messages
export const fetchErrorMessages = async () => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.GET_ERROR_MESSAGES}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching error messages:", error);
    throw error;
  }
};

export const publishJD = async (jobId, email) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.PUBLISH_JD,
      {
        job_id: jobId,
        email: email,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error publishing job description:", error);
    throw error;
  }
};

export const unPublishJD = async (jobId, email) => {
  try {
    const response = await Api.post(
      API_ENDPOINTS.UNPUBLISH_JD,
      {
        job_id: jobId,
        email: email,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error unpublishing job description:", error);
    throw error;
  }
};

export const editJD = async (jobId, email, updatedJD, jobCode) => {
  try {
    const response = await Api.post(
      `${API_ENDPOINTS.EDIT_JD}`,
      {
        job_id: jobId,
        email: email,
        edited_job_description: updatedJD,
        job_code: jobCode,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error editing JD:", error);
    throw error;
  }
};

export const regenJD = async (jobId, email, humanMessage, updatedJD) => {
  try {
    const response = await Api.post(
      `${API_ENDPOINTS.REGENERATE_JD}`,
      {
        job_id: jobId,
        email: email,
        human_message: humanMessage,
        edited_job_description: updatedJD,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error regerating JD:", error);
    throw error;
  }
};

export const retryJdGeneration = async (jobId, email) => {
  try {
    const response = await Api.post(API_ENDPOINTS.RETRY_JD_GENERATION, {
      job_id: jobId,
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Error retrying JD generation:", error);
    throw error;
  }
};


// Fetch Employee Profile 
export const getSBEmployeeProfile = async () => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.EMPLOYEE_VIEW}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching employee profile:", error);
    throw error;
  }
};

// Fetch Employee JD Skill Analysis (Skills required in current role).
// employeeId is optional: omit for the employee's own view (JWT-based),
// pass for the HR view to analyse a specific employee.
export const getSBEmployeeJdSkillAnalysis = async (employeeId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.EMPLOYEE_JD_SKILL_ANALYSIS, {
      params: employeeId ? { employee_id: employeeId } : undefined,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching employee JD skill analysis:", error);
    throw error;
  }
};

export const getEmployeeSummary = async (employeeId, employeeProfile = {}) => {
  try {
    const response = await Api.post(API_ENDPOINTS.EMPLOYEE_SUMMARY, {
      employee_id: employeeId,
      employee_profile: employeeProfile,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching employee summary:", error);
    throw error;
  }
};

// Fetch Employee Additional Skills
export const getSBEmployeeAdditionalSkills = async () => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.EMPLOYEE_ADDITIONAL_SKILLS}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching employee additional skills:", error);
    throw error;
  }
};

// Upload Sources to justify Proficiency Change
export const uploadProficiencySources = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.EMPLOYEE_UPLOADS, formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error uploading Source Files:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Delete Proficiency change Source File
export const deleteProficiencySource = async (payload) => {
  try {
    const response = await Api.delete(API_ENDPOINTS.EMPLOYEE_UPLOAD_DELETE, { data: payload });
    return response.data;
  } catch (error) {
    console.error("Error deleting Source File:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Submit Reasons for Change in Proficiency
export const submitProficiencyChangeReasons = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.EMPLOYEE_PROFICIENCY_CHANGE_REASONS, formData);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error submitting reasons for proficiency change:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Fetch Employee Proficiency Files
export const getSBEmployeeFiles = async () => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.EMPLOYEE_GET_FILES}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching employee proficiency source files:", error);
    throw error;
  }
};

// Fetch Employee Proficiency File Content
export const getSBEmployeeFileContent = async (payload) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.EMPLOYEE_GET_FILE_CONTENT}?file_id=${payload.file_id}`, { data: payload });
    return response.data;
  } catch (error) {
    console.error("Error fetching employee proficiency source file content:", error);
    throw error;
  }
};

// Fetch Employee Skill Proficiency Scale Definitions
export const getSBEmployeeScaleDefinition = async (payload) => {
  try {
    const response = await Api.get(API_ENDPOINTS.EMPLOYEE_SCALE_DEFINITION, { data: payload });
    return response.data;
  } catch (error) {
    console.error("Error fetching employee skill proficiency scale definition:", error);
    throw error;
  }
};

// Save files for reinfernece
export const saveSBEmployeeFiles = async () => {
  try {
    const response = await Api.post(API_ENDPOINTS.EMPLOYEE_FILE_SAVE);

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error submitting reasons for proficiency change:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Submit files for evaluation
export const submitSBEmployeeFiles = async () => {
  try {
    const response = await Api.post(API_ENDPOINTS.EMPLOYEE_SUBMIT);

    return response.data; // Return the response data
  } catch (error) {
    console.error("Error submitting files for evaluation:", error);
    throw error; // Throw error to handle it in the component
  }
};

// Submit files for evaluation
export const getNotifications = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_NOTIFICATIONS);
    return response.data;
  } catch (error) {
    console.error("Error getting notifications:", error);
    throw error;
  }
};

// FormData (file/image uploads) must NOT be sent as application/json, or axios
// serializes it to JSON and drops the files. Setting multipart/form-data steers
// axios to leave the body intact; the browser then fills in the boundary. Plain
// objects fall through to axios's JSON default.
const multipartConfigIfFormData = (payload) =>
  payload instanceof FormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : undefined;

// Submit Organization Feedback
export const submitUserFeedback = async (payload) => {
  try {
    const config = multipartConfigIfFormData(payload);
    const response = await Api.post(API_ENDPOINTS.UX_FEEDBACK, payload, config);
    return response.data;
  } catch (error) {
    console.error("Error submitting user feedback:", error);
    throw error;
  }
};

// Submit Ask Allie thumbs up/down feedback. payload is a plain object for
// text-only feedback, or FormData when the user attached screenshots/videos.
// FormData is tagged multipart/form-data so axios keeps the body intact and the
// browser sets the boundary; plain objects go as JSON.
export const submitAllieFeedback = async (payload) => {
  try {
    const config = multipartConfigIfFormData(payload);
    const response = await Api.post(API_ENDPOINTS.ALLIE_FEEDBACK, payload, config);
    return response.data;
  } catch (error) {
    console.error("Error submitting Allie feedback:", error);
    throw error;
  }
};

export const getAllPendingNotifications = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_ALL_NOTIFICATIONS);
    return response.data;
  } catch (error) {
    console.error("Error getting notifications:", error);

  }
};

export const clearAllPendingNotifications = async () => {
  try {
    const response = await Api.delete(API_ENDPOINTS.DELETE_ALL_NOTIFICATIONS);
    return response.data;
  } catch (error) {
    console.error("Error getting notifications:", error);

  }
};

export const getProfiles = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_PROFILES);
    return response.data.roles;
  } catch (error) {
    console.error("Error getting profile:", error);
    throw error
  }
}

export const getAllVersionSourceData = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_VERSION_SOURCE);
    return response.data;
  } catch (error) {
    console.error("Error fetching version source data:", error);
    throw error;
  }
}
// Function to send user behavior data to the backend
export const logUserBehavior = async (traceId, userId, eventType, eventData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.USER_ANALYTICS, {
      trace_id: traceId,
      user_id: userId,
      time_stamp: new Date().toISOString(),
      event_type: eventType,
      event_data: eventData,
    });
    return response.data;
  } catch (error) {
    console.error('Error logging user analytics:', error);
    throw error;
  }
};

export const createNewProfile = async (profileName) => {
  try {
    const response = await Api.post(API_ENDPOINTS.CREATE_NEW_PROFILE, {
      role_name: profileName
    });
    return response.data;
  } catch (error) {
    console.error("Error create profile:", error);
    throw error;
  }
}

export const getRoleWithEntitlements = async ({ selectedRoleId, selectedFeatureId }) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.GET_ROLE_WITH_ENTITLEMENTS}/role/${selectedRoleId}/feature/${selectedFeatureId}`);
    return response.data;
  } catch (error) {
    console.error("Error role with entitlements:", error);
    throw error;
  }
}

export const getFeatureWiseEntitlementList = async ({ selectedFeatureId }) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.GET_FEATURE_WISE_ENTITLEMENT_LIST}/${selectedFeatureId}`);
    return response.data;
  } catch (error) {
    console.error("Error feature wise entitlement list:", error);
    throw error;
  }
}

export const updateRoleWithEntitlements = async ({ selectedRoleId, selectedFeatureId, payload }) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.UPDATE_ROLE_WITH_ENTITLEMENTS}/role/${selectedRoleId}/feature/${selectedFeatureId}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error update profile:", error);
    throw error;
  }
}

export const getAllEmployees = async ({ pageNo, totalRecordsPerPage, complete }) => {
  try {
    let url = `${API_ENDPOINTS.GET_EMPLOYEES}?pageNo=${pageNo}&totalRecordsPerPage=${totalRecordsPerPage}`;

    if (complete !== TABLE_FILTER_CONSTANTS.ALL) {
      url += `&complete=${complete}`;
    }

    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console -- pre-existing on dev, not changed by this PR
    console.log("Error get all employees:", error);
    throw error;
  }
}

export const updateEmployee = async ({ updatedEmployeePayload, employeeId }) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.UPDATE_EMPLOYEE}/${employeeId}/update-employee`, updatedEmployeePayload)
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console -- pre-existing on dev, not changed by this PR
    console.log("Error update employees:", error)
    throw error;
  }
}

export const getManagerEmployeesListing = async ({ managerType }) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.MANAGER_EMPLOYEES_LISTING}?type=${managerType}`);
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console -- pre-existing on dev, not changed by this PR
    console.log("Error get all employees:", error);
    throw error;
  }
}


// --- PAIC APIs START ---
export const getPAICInferredJDs = async (payload) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_INFERRED_JDS}?email=${encodeURIComponent(payload.email)}`);
    return response.data;
  } catch (error) {
    console.error("Error getting inferred JDs (requisition view):", error);
    throw error;
  }
};

export const getPAICProjects = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.PAIC_GET_ORG_PROJECTS);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC projects:", error);
    throw error;
  }
};

export const createPAICProject = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_CREATE_ORG_PROJECT, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating PAIC project:", error);
    throw error;
  }
};

export const createPAICProjectRun = async (payload, refreshToken) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_CREATE_PROJECT_RUN, payload, {
      headers: { 'Refresh-Token': refreshToken },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating PAIC group run:", error);
    throw error;
  }
};

export const getPAICProjectStatus = async (projectId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_PROJECT_STATUS}?project_id=${projectId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC project status:", error);
    throw error;
  }
};

// Optional `config` (axios request config, e.g. a per-call timeout) is passed
// through as-is; callers that omit it keep the global Api defaults.
export const getPAICAgents = async (projectId, selectedK, config = {}) => {
  try {
    let url = `${API_ENDPOINTS.PAIC_GET_AGENTS}?project_id=${projectId}`;
    if (selectedK) {
      url += `&selected_k=${selectedK}`;
    }
    const response = await Api.get(url, config);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agents:", error);
    throw error;
  }
};

// Agent Library — every agent across all groups the user can access (owned / delegated / shared)
export const getAllAgents = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.PAIC_GET_ALL_AGENTS);
    return response.data;
  } catch (error) {
    console.error("Error getting all agents:", error);
    throw error;
  }
};

export const getPAICAgentsByRoles = async (selectionId, config = {}) => {
  try {
    const url = `${API_ENDPOINTS.PAIC_GET_AGENTS_BY_ROLES}?selection_id=${selectionId}`;
    const response = await Api.get(url, config);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agents by roles:", error);
    throw error;
  }
};

export const getPAICSelectedAgentsStats = async (payload, config = {}) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_SELECTED_AGENTS_STATS, payload, config);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC selected agents stats:", error);
    throw error;
  }
};

export const getPAICAgentSelections = async (projectId, config = {}) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_GET_AGENT_SELECTIONS}?project_id=${projectId}`, config);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agent selections:", error);
    throw error;
  }
};

export const insertPAICSelectedAgents = async (payload, refreshToken) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_INSERT_SELECTED_AGENTS, payload, {
      headers: { 'Refresh-Token': refreshToken },
    });
    return response.data;
  } catch (error) {
    console.error("Error inserting PAIC selected agents:", error);
    throw error;
  }
};

export const resubmitPAICSelection = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_RESUBMIT_SELECTION, payload);
    return response.data;
  } catch (error) {
    console.error("Error resubmitting PAIC selection:", error);
    throw error;
  }
};

export const getPAICAgentPRD = async (payload) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_GET_AGENT_PRD}/${payload.id}`);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agent PRD:", error);
    throw error;
  }
};

export const getPAICAgentJD = async (payload) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_GET_AGENT_JD}/${payload.id}`);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agent JD:", error);
    throw error;
  }
};

export const getAgentPRDVersions = async (agentId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.PAIC_GET_AGENT_PRD_VERSIONS}/${agentId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting PAIC agent PRD versions:", error);
    throw error;
  }
};

export const saveAgentPRD = async (agentId, agentPrdMd) => {
  try {
    const response = await Api.post(`${API_ENDPOINTS.PAIC_SAVE_AGENT_PRD}/${agentId}`, {
      agent_prd_md: agentPrdMd,
    });
    return response.data;
  } catch (error) {
    console.error("Error saving PAIC agent PRD:", error);
    throw error;
  }
};

export const getPAICJDs = async (selection_id) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.PAIC_GET_PAIC_JD}?selection_id=${selection_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching PAIC JDs:", error);
    throw error;
  }
};

export const getPAICAgentDetails = async (id) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.PAIC_GET_AGENT_DETAILS}/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching PAIC Agent Details:", error);
    throw error;
  }
};

export const getPAICRoleImpact = async (payload) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.PAIC_GET_ROLE_IMPACT}?selection_id=${payload.selection_id}&sb_unique_id=${payload.sb_unique_id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching PAIC Role Impact:", error);
    throw error;
  }
};

export const updatePAICAgent = async (agentId, payload) => {
  try {
    const response = await Api.patch(
      `${API_ENDPOINTS.PAIC_UPDATE_AGENT}/${agentId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating PAIC agent:", error);
    throw error;
  }
};

export const reinitiatePAICProjectRun = async (payload, refreshToken) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_REINITIATE_PROJECT_RUN, payload, {
      headers: { 'Refresh-Token': refreshToken },
    });
    return response.data;
  } catch (error) {
    console.error("Error reinitiating PAIC project run:", error);
    throw error;
  }
};

export const updatePAICArchiveStatus = async (projectId, archiveStatus) => {
  try {
    const response = await Api.put(
      `${API_ENDPOINTS.PAIC_UPDATE_ARCHIVE_STATUS}/${projectId}`,
      { archive_status: archiveStatus }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating PAIC archive status:", error);
    throw error;
  }
};

export const delegatePAICProject = async (
  projectId,
  delegatesToAdd = [],
  delegatesToRemove = []
) => {
  try {
    const payload = {
      delegates_to_add: delegatesToAdd,
      delegates_to_remove: delegatesToRemove,
    };

    const response = await Api.patch(
      `${API_ENDPOINTS.PAIC_DELEGATE_PROJECT}/${projectId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error delegating PAIC project:", error);
    throw error;
  }
};

export const sharePAICProject = async (
  projectId,
  sharedUsersToAdd = [],
  sharedUsersToRemove = []
) => {
  try {
    const payload = {
      shared_users_to_add: sharedUsersToAdd,
      shared_users_to_remove: sharedUsersToRemove,
    };

    const response = await Api.patch(
      `${API_ENDPOINTS.PAIC_SHARE_PROJECT}/${projectId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error sharing PAIC project:", error);
    throw error;
  }
};

export const transferPAICProjectOwner = async (projectId, email) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_TRANSFER_PROJECT_OWNER, {
      sb_project_unique_id: projectId,
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Error transferring PAIC project ownership:", error);
    throw error;
  }
};

export const transferPAICProjects = async (projectIds, email) => {
  try {
    const response = await Api.post(API_ENDPOINTS.PAIC_TRANSFER_PROJECTS, {
      sb_project_unique_ids: projectIds,
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Error transferring PAIC projects:", error);
    throw error;
  }
};

// --- PAIC API END---

// --- ORG REDESIGN (Org Design) API START ---


export const getOrgRedesignCurrentOrgStructure = async (email, signal) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ORG_REDESIGN_CURRENT_ORG_STRUCTURE}?email=${encodeURIComponent(email)}`,
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting current org structure:", error);
    throw error;
  }
};

export const getOrgRedesignOrganizationView = async (email, signal) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ORG_REDESIGN_ORGANIZATION_VIEW}?email=${encodeURIComponent(email)}`,
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting organization view:", error);
    throw error;
  }
};

export const getOrgRedesignPaicProjects = async (signal) => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_REDESIGN_PAIC_PROJECTS, { signal });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting Org Redesign PAIC projects:", error);
    throw error;
  }
};

export const getOrgRedesignPaicProjectHandshake = async (paicProjectId, signal) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ORG_REDESIGN_PAIC_PROJECT_HANDSHAKE}/${paicProjectId}/handshake`,
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting PAIC project handshake:", error);
    throw error;
  }
};

export const getOrgRedesignPaicProjectVersions = async (paicProjectId, signal) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.ORG_REDESIGN_PAIC_PROJECT_VERSIONS}/${paicProjectId}/versions`,
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting PAIC project versions:", error);
    throw error;
  }
};


export const createOrgRedesignVersion = async (payload, refreshToken, signal) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_REDESIGN_CREATE_VERSION, payload, {
      headers: { "Refresh-Token": refreshToken },
      signal,
    });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error creating Org Redesign version:", error);
    throw error;
  }
};

export const getOrgRedesignVersion = async (orgRedesignId, signal) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.ORG_REDESIGN_VERSION}/${orgRedesignId}`, {
      signal,
    });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting Org Redesign version:", error);
    throw error;
  }
};

// Lightweight, org-wide list of every PUBLISHED Org Redesign version (Org
// Redesign API doc §8) — no roles/trees, just enough to render a picker row.
// Distinct from getOrgRedesignPaicProjectVersions, which lists PAIC
// agent-selection versions scoped to one project.
export const getOrgRedesignVersions = async (signal) => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_REDESIGN_VERSIONS, { signal });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting Org Redesign versions:", error);
    throw error;
  }
};

// PUT /version/{id}/update (Org Redesign API doc §11) — persists the user's
// in-workspace edits (existing-role overrides + newly proposed roles) onto
// an existing version's proposed changes, in place. Requires at least one of
// existing_roles/new_roles to be non-empty (backend 400s otherwise).
export const updateOrgRedesignVersion = async (orgRedesignId, payload, signal) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.ORG_REDESIGN_VERSION}/${orgRedesignId}/update`, payload, { signal });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error updating Org Redesign version:", error);
    throw error;
  }
};

// PUT /version/{id}/update_archive_status — archives ("ARCHIVED") or restores
// ("ACTIVE") one Org Redesign version. Backend gates it on
// org_redesign:delete and only lets the version's creator change it; an
// archived version drops out of GET /versions but its data is kept.
export const updateOrgRedesignArchiveStatus = async (orgRedesignId, archiveStatus, signal) => {
  try {
    const response = await Api.put(
      `${API_ENDPOINTS.ORG_REDESIGN_VERSION}/${orgRedesignId}/update_archive_status`,
      { archive_status: archiveStatus },
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error updating Org Redesign archive status:", error);
    throw error;
  }
};

// Baseline/target/projected budget + FTE deltas for a single, already-
// persisted Org Redesign version (doc §9) — used for the PAIC-sourced
// workspace flow, where a real sb_org_redesign_unique_id already exists.
export const getOrgRedesignVersionBudget = async (orgRedesignId, payload, signal) => {
  try {
    const response = await Api.post(`${API_ENDPOINTS.ORG_REDESIGN_VERSION}/${orgRedesignId}/budget`, payload || {}, { signal });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting Org Redesign version budget:", error);
    throw error;
  }
};

// Same computation, but for the whole live org rather than one persisted
// version (doc §10) — used for the Current Org workspace flow, which has no
// backing org_redesign_id until the draft is actually saved. Unlike the
// per-version endpoint above, a request body is required even if empty.
export const getCurrentOrgBudget = async (payload, signal) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_REDESIGN_CURRENT_ORG_BUDGET, payload || {}, { signal });
    return response.data;
  } catch (error) {
    if (error.code !== "ERR_CANCELED") console.error("Error getting current org budget:", error);
    throw error;
  }
};

// --- ORG REDESIGN (Org Design) API END ---

// --- APPROVAL WORKFLOW API ---

export const getApprovalCandidates = async () => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.APPROVAL_CANDIDATES}?type=approver`);
    return response.data;
  } catch (error) {
    console.error("Error fetching approval candidates:", error);
    throw error;
  }
};



// staleCheck: the latest version the page loaded ({ latest_sb_unique_id, latest_last_updated_time_stamp }),
// required to send a JD as it is (a draft, a NEW v1.00); the resubmit after NEEDS_REVISION sends none.
export const submitForApproval = async (sbUniqueId, assignedApproverEmail, staleCheck = {}) => {
  try {
    const response = await Api.post(API_ENDPOINTS.APPROVAL_SUBMIT, {
      sb_unique_id: sbUniqueId,
      assigned_approver: assignedApproverEmail,
      ...staleCheck,
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting for approval:", error);
    throw error;
  }
};

export const getApprovalDiff = async (jobId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.APPROVAL_DIFF}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching approval diff:", error);
    throw error;
  }
};

export const submitApprovalReview = async (jobId, decisions, editorValue) => {
  try {
    const response = await Api.post(API_ENDPOINTS.APPROVAL_REVIEW, {
      job_id: jobId,
      decisions: decisions,
      markdown: editorValue
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting approval review:", error);
    throw error;
  }
};

export const getApprovalDashboard = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.APPROVAL_DASHBOARD);
    return response.data;
  } catch (error) {
    console.error("Error fetching approval dashboard:", error);
    throw error;
  }
};

export const getHistoryList = async (jobId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.APPROVAL_HISTORY_LIST}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching history list:", error);
    throw error;
  }
};

export const compareCustomVersions = async (jobId, versionLeft, versionRight) => {
  try {
    const response = await Api.post(API_ENDPOINTS.APPROVAL_COMPARE_CUSTOM, {
      job_id: jobId,
      version_left: versionLeft,
      version_right: versionRight
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching custom comparison:", error);
    throw error;
  }
};

export const getRevisionContext = async (jobId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.APPROVAL_REVISION_CONTEXT}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching revision context:", error);
    throw error;
  }
};

export const insertBqRow = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.INSERT_BIGQUERY, payload);
    return response.data;
  } catch (error) {
    console.error("Error inserting BQ row:", error);
    throw error;
  }
};

export const saveRevisionDraft = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.APPROVAL_SAVE_REVISION, payload);
    return response.data;
  } catch (error) {
    console.error("Error saving revision draft:", error);
    throw error;
  }
};

export const getFullJobDetail = async (jobId, email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.GET_FULL_JOB_DETAIL}?job_id=${jobId}&email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching full job detail from Mongo:", error);
    throw error;
  }
};

export const fetchPersistentNotifications = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.PERSISTENT_NOTIFICATIONS);
    // Returns the array of notification objects
    return response.data.notifications || [];
  } catch (error) {
    console.error("Error fetching persistent notifications:", error);
    return [];
  }
};

export const clearAllPersistentNotifications = async () => {
  return await Api.delete(API_ENDPOINTS.CLEAR_ALL_PERSISTENT);
};

export const clearSpecificNotification = async (jobId) => {
  return await Api.post(API_ENDPOINTS.CLEAR_SPECIFIC_PERSISTENT, { entity_id: jobId });
};

export const compareMarkdowns = async (markdownLeft, markdownRight) => {
  try {
    const response = await Api.post(API_ENDPOINTS.APPROVAL_COMPARE_MARKDOWNS, {
      markdown_left: markdownLeft,
      markdown_right: markdownRight,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching custom comparison:", error);
    throw error;
  }
};

export const addJobCode = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ADD_JOB_CODE, payload);
    return response.data;
  } catch (error) {
    console.error("Error add job code:", error);
    throw error;
  }
};

export const selfPublishJd = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.SELF_PUBLISH_JD, payload);
    return response.data;
  } catch (error) {
    console.error("Error saving revision draft:", error);
    throw error;
  }
};

export const getOrgTechContext = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_CONTEXT_TOOL_CATALOG);
    return response.data;
  } catch (error) {
    console.error("Error fetching org tech context:", error);
    throw error;
  }
};

export const systemUploadInsert = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.SYSTEM_UPLOAD_INSERT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error inserting system upload:', error);
    throw error;
  }
};

export const saveOrgTechContext = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_CONTEXT_TOOL_CATALOG, payload);
    return response.data;
  } catch (error) {
    console.error("Error saving org tech context:", error);
  }
}

export const fetchAllEmployees = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ALL_EMPLOYEES);
    return response.data;
  } catch (error) {
    console.error("Error fetching all employees:", error);
    throw error;
  }
};

// Single unified group endpoint — mirrors the employee_jd_skill_analysis contract.
// Returns { personProfileAnalysis: { skills, tacit_knowledge, tasks, profile_summary, ... }, current_vs_required_SBI_score }.
// Called once on navigation; the result is forwarded via router state to both the
// group-skills-map and group-skills-proficiency pages so neither page re-fetches.
export const fetchGroupJdSkillAnalysis = async (selectedEmployees) => {
  try {
    const response = await Api.post(API_ENDPOINTS.GROUP_JD_SKILL_ANALYSIS, {
      selected_employees: selectedEmployees,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching group JD skill analysis:", error);
    throw error;
  }
};

export const fetchGroupSummary = async (employeeIds) => {
  try {
    const response = await Api.post(API_ENDPOINTS.GROUP_SUMMARY, {
      employee_ids: employeeIds,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching group summary:", error);
    throw error;
  }
};

export const getEmployeeFunctionAccess = async (email) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.EMPLOYEE_FUNCTION_ACCESS}?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching employee function access list:', error);
    throw error;
  }
};

export const getRegistryManagement = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_CONTEXT_TOOLS_SELECTION);
    return response.data;
  } catch (error) {
    console.error("Error fetching registry management:", error);
  }
}

export const updateEmployeeFunctionAccess = async (payload) => {
  try {
    const response = await Api.put(
      API_ENDPOINTS.UPDATE_EMPLOYEE_ACCESS,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Error updating employee function access:', error);
    throw error;
  }
};

export const saveRegistryManagement = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_CONTEXT_TOOLS_SELECTION, payload);
    return response.data;
  } catch (error) {
    console.error("Error saving registry management:", error);
    throw error;
  }
}
export const systemUpload = async (sbUniqueId, email) => {
  try {
    const response = await Api.post(API_ENDPOINTS.SYSTEM_UPLOAD, {
      sb_unique_id: sbUniqueId,
      email
    });
    return response.data;
  } catch (error) {
    console.error('Error triggering system upload:', error);
    throw error;
  }
};

// --- ROLE REPORTING APIs START ---

export const getRoleReportingView = async (email, params = {}) => {
  try {
    const queryParts = [`email=${encodeURIComponent(email)}`];

    if (params.limit) queryParts.push(`limit=${params.limit}`);
    if (params.offset != null) queryParts.push(`offset=${params.offset}`);

    const url = `${API_ENDPOINTS.ROLE_REPORTING_VIEW}?${queryParts.join("&")}`;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching role reporting view:", error);
    throw error;
  }
};

export const updateRoleReporting = async (sbUniqueId, payload) => {
  try {
    const response = await Api.patch(
      `${API_ENDPOINTS.ROLE_REPORTING_UPDATE}/${sbUniqueId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating role reporting:", error);
    throw error;
  }
};

export const downloadRoleReportingTemplate = async (email) => {
  try {
    const response = await Api.get(API_ENDPOINTS.ROLE_REPORTING_DOWNLOAD_TEMPLATE, {
      params: { email },
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error("Error downloading role reporting template:", error);
    throw error;
  }
};

export const bulkUploadRoleReportingTo = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.ROLE_REPORTING_BULK_UPLOAD, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting role reporting bulk upload:", error);
    throw error;
  }
};

// --- ROLE REPORTING APIs END ---

// --- JD ARCHIVE APIs START ---

export const updateJdArchiveStatus = async (sbUniqueId, archiveStatus) => {
  try {
    const response = await Api.patch(
      `${API_ENDPOINTS.JD_ARCHIVE_UPDATE_STATUS}/${sbUniqueId}`,
      { archive_status: archiveStatus }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating JD archive status:", error);
    throw error;
  }
};

// --- JD ARCHIVE APIs END ---

// --- COMPENSATION MANAGEMENT APIs START ---

export const getCompRequisitionView = async (email, params = {}) => {
  try {
    const queryParts = [`email=${encodeURIComponent(email)}`];

    if (params.limit) queryParts.push(`limit=${params.limit}`);
    if (params.offset != null) queryParts.push(`offset=${params.offset}`);
    if (params.compensation_source) queryParts.push(`compensation_source=${encodeURIComponent(params.compensation_source)}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.function) queryParts.push(`function=${encodeURIComponent(params.function)}`);
    if (params.sub_function) queryParts.push(`sub_function=${encodeURIComponent(params.sub_function)}`);

    const url = `${API_ENDPOINTS.COMP_REQUISITION_VIEW}?${queryParts.join("&")}`;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching compensation requisition view:", error);
    throw error;
  }
};

export const getCompensation = async (jobId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.COMP_GET_COMPENSATION}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching compensation:", error);
    throw error;
  }
};

export const insertCompensation = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.COMP_INSERT_COMPENSATION, payload);
    return response.data;
  } catch (error) {
    console.error("Error inserting compensation:", error);
    throw error;
  }
};

export const updateCompensation = async (jobId, payload) => {
  try {
    const response = await Api.put(API_ENDPOINTS.COMP_UPDATE_COMPENSATION, { job_id: jobId, ...payload });
    return response.data;
  } catch (error) {
    console.error("Error updating compensation:", error);
    throw error;
  }
};

export const getJdIdentifierConfig = async (email) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.COMP_JD_IDENTIFIER_CONFIG}?email=${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching JD identifier config:", error);
    throw error;
  }
};

// --- CURRENCY SETTINGS APIs ---

export const getCurrencySettings = async (email) => {
  try {
    const response = await Api.get(
      `${API_ENDPOINTS.COMP_CURRENCY_SETTINGS}?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching currency settings:", error);
    throw error;
  }
};

export const updateCurrencySettings = async (email, payload) => {
  try {
    const response = await Api.put(
      `${API_ENDPOINTS.COMP_CURRENCY_SETTINGS}?email=${encodeURIComponent(email)}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating currency settings:", error);
    throw error;
  }
};

// --- BULK COMPENSATION UPLOAD/DOWNLOAD APIs ---

export const downloadCompensationTemplate = async (email, { search, filters } = {}) => {
  try {
    const queryParts = [`email=${encodeURIComponent(email)}`];

    if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

    // Only forward filters that actually have an active (non-empty) selection —
    // Ant Table's tableFilters includes every filterable column with a null
    // value once touched, and the backend expects unfiltered columns omitted.
    const activeFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(([, value]) => Array.isArray(value) && value.length > 0),
    );
    if (Object.keys(activeFilters).length > 0) {
      queryParts.push(`filters=${encodeURIComponent(JSON.stringify(activeFilters))}`);
    }

    const url = `${API_ENDPOINTS.COMP_DOWNLOAD_TEMPLATE}?${queryParts.join("&")}`;
    const response = await Api.get(url, { responseType: 'blob' });
    return response;
  } catch (error) {
    console.error("Error downloading compensation template:", error);
    throw error;
  }
};

export const bulkUploadCompensation = async (email, file) => {
  try {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('file', file);
    const response = await Api.post(
      API_ENDPOINTS.COMP_BULK_UPLOAD,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading compensation data:", error);
    throw error;
  }
};

// --- COMPENSATION MANAGEMENT APIs END ---


export const submitBulkJdUpload = async (formData) => {
  try {
    const response = await Api.post(API_ENDPOINTS.BULK_JD_UPLOAD, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  } catch (error) {
    console.error('Error submitting bulk JD upload:', error);
    throw error;
  }
};

export const downloadBulkJdTemplate = async (email) => {
  try {
    const response = await Api.get(API_ENDPOINTS.BULK_JD_DOWNLOAD_TEMPLATE, {
      params: { email },
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error downloading bulk JD template:', error);
    throw error;
  }
};

// --- JD Templates ---

export const getJdTemplates = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_ALL_JD_TEMPLATES);
    return response.data;
  } catch (error) {
    console.error("Error fetching JD templates:", error);
    throw error;
  }
};
  
export const downloadOrgHierarchyTemplate = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.ORG_HIERARCHY_DOWNLOAD_TEMPLATE, {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error downloading org hierarchy template:', error);
    throw error;
  }
};

export const getJdTemplate = async (id) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.GET_JD_TEMPLATE}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching JD template:", error);
    throw error;
  }
};
 
export const uploadOrgHierarchyEmployees = async (file, columnMap = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (columnMap) formData.append('column_map', JSON.stringify(columnMap));
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_HIERARCHY_UPLOAD, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data; // { upload_id, total_rows, mapped_count, needs_action_count, flagged_count, status }
  } catch (error) {
    console.error('Error uploading org hierarchy employees:', error);
    throw error;
  }
};

export const createJdTemplate = async ({ templateName, legalEntity, country }) => {
  try {
    const response = await Api.post(API_ENDPOINTS.CREATE_JD_TEMPLATE, {
      org_jd_template_name:  templateName,
      sb_org_template_value: country,
      sb_template_legal_entity: legalEntity,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating JD template:", error);
    throw error;
  }
};

export const updateJdTemplate = async (id, { templateName, country, markdown }) => {
  try {
    const response = await Api.patch(`${API_ENDPOINTS.UPDATE_JD_TEMPLATE}/${id}`, {
      org_jd_template_name:  templateName,
      sb_org_template_value: country,
      org_jd_template_md: markdown,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating JD template:", error);
    throw error;
  }
};

export const getJdMergeFields = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_JD_MERGE_FIELDS);
    return response.data;
  } catch (error) {
    console.error("Error fetching JD merge fields:", error);
    throw error;
  }
};

export const organizationStructure = async (payload) => {
  try {
    const response = await Api.put(API_ENDPOINTS.ORGANIZATION_STRUCTURE, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating organization structure:", error);
    throw error;
  }
};


export const processOrgHierarchyMapping = async (uploadId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.ORG_HIERARCHY_MAPPING_RESULTS}/${uploadId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching org hierarchy mapping results:', error);
    throw error;
  }
};

export const confirmOrgHierarchyMapping = async (mappings) => {
  const payload = mappings.length === 1
    ? { mapping_id: mappings[0].mapping_id, selected_requisition: mappings[0].selected_requisition }
    : { resolutions: mappings };
  try {
    const response = await Api.post(API_ENDPOINTS.ORG_HIERARCHY_CONFIRM_MAPPING, payload);
    return response.data;
  } catch (error) {
    console.error('Error confirming org hierarchy mapping:', error);
    throw error;
  }
};

export const onboardMappedOrgHierarchyEmployees = async ({ uploadId, dryRun = false }) => {
  try {
    const payload = {
      upload_id: uploadId,
    };
    if (dryRun === true) payload.dry_run = true;

    const response = await Api.post(API_ENDPOINTS.ORG_HIERARCHY_ONBOARD_MAPPED_EMPLOYEES, payload);
    return response.data;
  } catch (error) {
    console.error('Error onboarding mapped org hierarchy employees:', error);
    throw error;
  }
};

export const getOrgHierarchyEmployees = async ({ search = '', limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.set('search', search);
    const response = await Api.get(`${API_ENDPOINTS.ORG_HIERARCHY_EMPLOYEES}?${params.toString()}`);
    return response.data; // { total_count, limit, offset, employees }
  } catch (error) {
    console.error('Error fetching org hierarchy employees:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Agent Builder
// ---------------------------------------------------------------------------

export const startAgentBuild = async (agentUniqueId, force = false, contextUrl = "") => {
  try {
    const response = await Api.post(API_ENDPOINTS.AGENT_BUILDER_START, {
      agent_unique_id: agentUniqueId,
      ...(force && { force: true }),
      ...(contextUrl && { context_url: contextUrl }),
    });
    return response.data;
  } catch (error) {
    console.error("Error starting agent build:", error);
    throw error;
  }
};

export const submitAgentBuildAnswer = async (buildId, answer) => {
  try {
    const response = await Api.post(`${API_ENDPOINTS.AGENT_BUILDER_ANSWER}${buildId}`, { answer });
    return response.data;
  } catch (error) {
    console.error("Error submitting agent build answer:", error);
    throw error;
  }
};

export const getAgentBuildStatus = async (buildId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.AGENT_BUILDER_STATUS}${buildId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAgentBuildDownloadUrl = async (buildId) => {
  try {
    const response = await Api.get(`${API_ENDPOINTS.AGENT_BUILDER_DOWNLOAD}${buildId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting agent build download URL:", error);
    throw error;
  }
};

// ── Legal consent ──────────────────────────────────────────────────────────────
// Cache the consent lookup for the session so role switches / component
// remounts don't refetch. Stores the in-flight promise (dedupes concurrent
// calls) and its result; invalidated by saveConsent so the next read is fresh.
let _consentPromise = null;

const fetchConsent = async () => {
  try {
    const { data } = await Api.get(API_ENDPOINTS.LEGAL_CONSENT);
    return data;
  } catch (err) {
    _consentPromise = null; // don't cache failures — allow a retry
    throw err;
  }
};

export const getConsent = () => {
  if (!_consentPromise) _consentPromise = fetchConsent();
  return _consentPromise;
};

export const saveConsent = async (payload) => {
  const { data } = await Api.post(API_ENDPOINTS.LEGAL_CONSENT, payload);
  _consentPromise = null; // invalidate cache; next getConsent refetches fresh
  return data;
};

export const getJdBanner = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.GET_JD_BANNER);
    return response.data; // { url } — url is null when the org has no banner
  } catch (error) {
    console.error("Error fetching JD banner:", error);
    throw error;
  }
};

export const uploadJdBanner = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await Api.post(API_ENDPOINTS.UPLOAD_JD_BANNER, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data; // { url, rows_updated }
  } catch (error) {
    console.error("Error uploading JD banner:", error);
    throw error;
  }
};

// --- TALENT MOBILITY APIs ---

export const getTalentMobilityThresholds = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_THRESHOLDS);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility thresholds:", error);
    throw error;
  }
};

export const updateTalentMobilityThresholds = async (payload) => {
  try {
    const response = await Api.put(API_ENDPOINTS.TALENT_MOBILITY_THRESHOLDS, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating Talent Mobility thresholds:", error);
    throw error;
  }
};

export const getTalentMobilityEmployees = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_EMPLOYEES);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility employees:", error);
    throw error;
  }
};

// Payload: { user_id } — sb_user_unique_id of the employee. Returns name,
// email, currentRole (jdSbUniqueId, roleName, jobOpeningStatus, joiningDate,
// sri, jdMetadataJson) and previousRoles.
export const getTalentMobilityPeopleProfileDetails = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_PEOPLE_PROFILE_DETAILS, payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility people profile details:", error);
    throw error;
  }
};

// jd_sb_unique_id — the role's JD. occupantUserIds — every current occupant
// of the role (empty for a role nobody holds); the backend excludes their
// current_employees teammates (same manager_emp_id) from the candidate pool,
// so a teammate of an outgoing occupant never shows up as their own
// successor. Returns { employees: [{
// personProfileId, name, smi, profile_score_flag, criticalSkills,
// shortlisted }] }. `shortlisted` comes from the JD's own shared shortlist
// row (one per role, not per occupant — see addTalentMobilityShortlist
// below), so every occupant of a multi-occupant role sees the same flags.
export const getTalentMobilityRoleSuccessorAnalytics = async (jdSbUniqueId, occupantUserIds) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_ROLE_SUCCESSOR_ANALYTICS, {
      jd_sb_unique_id: jdSbUniqueId,
      user_ids: occupantUserIds,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility role successor analytics:", error);
    throw error;
  }
};

// One employee's skills against one JD — the JD's skill list (top 5 by
// importance) with the person's profile_proficiency merged in. Returns
// { skills: [{ skill, skill_id, proficiency_needed, profile_proficiency,
// importance, contextual_notes, ... }] }.
export const getTalentMobilityEmployeeSkills = async (userId, currentJdId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_EMPLOYEE_SKILLS, {
      params: { user_id: userId, current_jd_id: currentJdId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility employee skills:", error);
    throw error;
  }
};

// jd_sb_unique_id — the role's JD. Returns { jd_sb_unique_id, employees:
// [{ userId, name, profile_score, critical_skills }] } — the people ACTIVE
// on the JD (its current occupants), the inverse of role-successor-analytics.
export const getTalentMobilityJdActiveEmployees = async (jdSbUniqueId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_JD_ACTIVE_EMPLOYEES, {
      params: { jd_sb_unique_id: jdSbUniqueId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility JD active employees:", error);
    throw error;
  }
};

// user_id — the employee. Returns { userId, nextRoles: [{ jd_sb_unique_id,
// role_name, jd_metadata_json, profile_score, critical_skills }] } — the JDs
// the employee is profiled against, minus any role they already hold.
export const getTalentMobilityNextRoles = async (userId) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_NEXT_ROLES, { user_id: userId });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility next roles:", error);
    throw error;
  }
};

// Payload: { jd_sb_unique_id, shortlisted_user_id, user_ids, notify } —
// appends shortlisted_user_id to the ROLE's shared shortlist
// (global_talentmobility_plans.shortlisted_users, one row per
// jd_sb_unique_id — every occupant of the role sees the same list, there's
// no per-occupant shortlist anymore). created_by is never sent — the
// backend derives it from the JWT. Idempotent: shortlisting the same person
// twice returns the same success response. notify = { employee,
// line_manager, hiring_manager } bools (the confirm dialog's checkboxes) —
// each checked audience gets a real notification ("{name} will be
// shortlisted for {job title}.") resolved and sent server-side; the
// response's `notifications` map reports sent/skipped/not_requested per
// audience (also persisted on the plan row, overwritten on every call).
// user_ids (optional) — this role's OPEN occupants, each as { user_id,
// job_opening_status, is_job_discoverable } (either flag optional); the
// backend upserts each into open_users_global_plans, the same table PUT
// /job-opening-status writes, so shortlisting also captures this role's
// occupants' Open/Discoverable snapshot without a separate checkbox toggle.
// An invalid or empty entry is skipped server-side — never fails the
// shortlist write itself. See CandidateProfileV2.jsx's confirmShortlist for
// where this list comes from (Role Detail's router state).
export const addTalentMobilityShortlist = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_SHORTLIST, payload);
    return response.data;
  } catch (error) {
    console.error("Error adding Talent Mobility shortlist:", error);
    throw error;
  }
};

// Payload: { jd_sb_unique_id, shortlisted_user_id } — Undo Shortlist button.
// Drops shortlisted_user_id from the ROLE's shared shortlist
// (global_talentmobility_plans.shortlisted_users). removed_by is never
// sent — the backend derives it from the JWT. There's no notify checkbox
// here: the backend re-notifies whichever audiences were notified when
// this person was (last) shortlisted for this role, using the plan row's
// own stored notifications flags ("{name} has been removed from the
// shortlist for {job title}.").
export const removeTalentMobilityShortlist = async (payload) => {
  try {
    const response = await Api.delete(API_ENDPOINTS.TALENT_MOBILITY_SHORTLIST, { data: payload });
    return response.data;
  } catch (error) {
    console.error("Error removing Talent Mobility shortlist:", error);
    throw error;
  }
};

// jd_sb_unique_id — a Workforce Planning demand role's JD. Returns the
// people matched to it with their redeployment availability, feeding the
// Candidates step's available/unavailable split.
export const getTalentMobilityWorkforcePeopleAvailability = async (jdSbUniqueId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_WORKFORCE_PEOPLE_AVAILABILITY, {
      params: { jd_sb_unique_id: jdSbUniqueId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility workforce people availability:", error);
    throw error;
  }
};

// Payload: { plan_name, shortlisted_employee_targets: { <demand_role_jd>:
// [user_id, ...] }, target_positions: { <demand_role_jd>: number } } —
// submits the whole workforce growth draft useWorkforcePlanStore builds up
// across the Workforce Planning screens, all demand roles in one call.
export const addTalentMobilityWorkforcePlan = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_WORKFORCE_PLAN, payload);
    return response.data;
  } catch (error) {
    console.error("Error submitting Talent Mobility workforce plan:", error);
    throw error;
  }
};

// PUT counterpart to addTalentMobilityWorkforcePlan — syncs an already-saved
// plan's detail rows to the current draft (same payload shape) instead of
// creating a new plan_id, keyed by the plan_id a prior create/update
// returned (see useWorkforcePlanActions).
export const updateTalentMobilityWorkforcePlan = async (planId, payload) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.TALENT_MOBILITY_WORKFORCE_PLAN}/${planId}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating Talent Mobility workforce plan:", error);
    throw error;
  }
};

// One saved plan's stored workforce_user_plan rows, read back in the same
// per-role shape (jd_sb_unique_id, shortlisted_users, target_position,
// notifications) the create/update payload used. Returns { plan_id,
// workforce_plans: [...] }. Powers View Planning's Edit action, which loads
// this straight into useWorkforcePlanStore (see loadPlan) before navigating
// into Workforce Planning to keep editing the same plan.
export const getWorkforcePlanDetails = async (planId) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_WORKFORCE_PLAN_DETAILS, { plan_id: planId });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility workforce plan details:", error);
    throw error;
  }
};

// The org's saved redeployment plans, newest first — one record per
// talent_mobility_plans row with plan_type "redeploy": plan_id, plan_name,
// created_by (id), created_by_name, created_at, and job_length (how many
// redeployment_user_plan role rows the plan holds, counted from the
// redeployment table in BigQuery). Returns { plans: [...] }.
export const getViewPlanningRedeploymentPlans = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_REDEPLOYMENT_PLANS);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility redeployment plans:", error);
    throw error;
  }
};

// The org's saved workforce plans, newest first — one record per
// talent_mobility_plans row with plan_type "workforce": plan_id, plan_name,
// created_by (id), created_by_name, created_at, and job_length (how many
// workforce_user_plan demand-role rows the plan holds, counted from the
// workforce table in BigQuery). Returns { plans: [...] }.
export const getViewPlanningWorkforcePlans = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_WORKFORCE_PLANS);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility workforce plans:", error);
    throw error;
  }
};

// Stored workforce plan job rows, newest first — one record per
// workforce_user_plan entry: the demand role's JD columns plus
// workforce_user_plan_id / plan_id / target_position / shortlisted_users.
// Pass a plan_id to narrow to one plan; omit it for every stored plan row.
// Returns { jobs: [...] }.
export const getViewPlanningWorkforcePlanJobs = async (planId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_WORKFORCE_PLAN_JOBS, {
      params: planId ? { plan_id: planId } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility workforce plan jobs:", error);
    throw error;
  }
};

// ── View Planning ▸ PAIC Redesign Plans ───────────────────────────────────────
// PAIC Redesign Plan tab — same shape conventions as the
// redeployment/workforce view-planning endpoints.

// PAIC endpoints log only the status and error_code — the axios error object
// carries the request's Authorization header (COMP-1).
const logPaicApiError = (label, error) =>
  console.error(label, error?.response?.status, error?.response?.data?.error_code);

// GET — saved PAIC redesign plans (talent_mobility_plans rows with plan_type
// "paic_org_redesign"). Returns { plans: [{ plan_id, plan_name, created_by,
// created_by_name, created_at, job_length, organization_count, project_name,
// organization }] } — job_length = role count; project_name = the plan's
// version name (the tab's "Project Name" column); organization = the version
// the plan opens on ({ sb_org_redesign_unique_id, org_redesign_name,
// project_name, version }, or null when none is visible), passed straight to
// the roles endpoint below.
export const getViewPlanningPaicRedesignPlans = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_PAIC_REDESIGN_PLANS);
    return response.data;
  } catch (error) {
    logPaicApiError("Error fetching PAIC redesign plans:", error);
    throw error;
  }
};

// GET ?plan_id=&sb_org_redesign_unique_id= — the roles one version of the
// plan shortlisted employees for (the only roles a plan stores). Returns {
// plan_id, sb_org_redesign_unique_id, roles: [{ role_key, jd_sb_unique_id,
// title, change_type (the version's status, e.g. "REDUCED" | "REDUNDANT"),
// before_hc, after_hc (the plan's approved headcount when it has one),
// agents, rationale, target_position, shortlisted_count }] } —
// PAIC_ROLE_TABS maps change_type onto the role tabs.
export const getViewPlanningPaicRedesignPlanRoles = async (planId, orgRedesignId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_PAIC_REDESIGN_PLAN_ROLES, {
      params: { plan_id: planId, sb_org_redesign_unique_id: orgRedesignId },
    });
    return response.data;
  } catch (error) {
    logPaicApiError("Error fetching PAIC redesign plan roles:", error);
    throw error;
  }
};

// GET — the employees one PAIC plan shortlisted for a role (View Planning
// drill-down), read from the plan's stored roles. Returns { plan_id,
// sb_org_redesign_unique_id, role_key, jd_sb_unique_id, target_position,
// employees: [{ employee_unique_id, emp_id, name, targeted_roles,
// shortlist_reason, smi (score against this role, or null) }] } — shown in
// the role's expanded row (PaicRoleEmployeesPanel).
export const getViewPlanningPaicRedesignRoleEmployees = async (planId, orgRedesignId, roleKey) => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_PAIC_REDESIGN_ROLE_EMPLOYEES, {
      params: { plan_id: planId, sb_org_redesign_unique_id: orgRedesignId, role_key: roleKey },
    });
    return response.data;
  } catch (error) {
    logPaicApiError("Error fetching PAIC redesign plan role employees:", error);
    throw error;
  }
};

// One plan role row's stored available employees (View Planning drill-down)
// — reads the redeployment_user_plan row's available_employee_targets JSON
// by redeploy_plan_unique_id. Each record: employee_unique_id, resolved
// name, and targeted_roles ([{ jd_sb_unique_id, title }]).
// Returns { redeploy_plan_unique_id, plan_id, jd_sb_unique_id,
// target_position, employees: [...] }.
export const getViewPlanningRedeploymentRoleEmployees = async (redeployPlanUniqueId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_REDEPLOYMENT_ROLE_EMPLOYEES, {
      params: { redeploy_plan_unique_id: redeployPlanUniqueId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility redeployment role employees:", error);
    throw error;
  }
};

// Stored redeployment plan role rows, newest first — one record per
// redeployment_user_plan entry, in the /talent-mobility/employees record
// shape plus redeploy_plan_unique_id / plan_id / target_position. Pass a
// plan_id to narrow to one plan; omit it for every stored plan row.
// Returns { roles: [...] }.
export const getViewPlanningRedeploymentPlanRoles = async (planId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.VIEW_PLANNING_REDEPLOYMENT_PLAN_ROLES, {
      params: planId ? { plan_id: planId } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility redeployment plan roles:", error);
    throw error;
  }
};

// Payload: { plan_name, available_employee_targets: { <current_role_jd>:
// [{ employee_unique_id, targeted_jd_sb_unique_ids }] }, target_positions:
// { <current_role_jd>: number } } — submits the whole redeployment draft
// useRedeploymentPlanStore builds up across the Redeployment Center screens,
// all roles in one call (see getRedeploymentPlanPayload).
export const addTalentMobilityRedeploymentPlan = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_REDEPLOYMENT_PLAN, payload);
    return response.data;
  } catch (error) {
    console.error("Error submitting Talent Mobility redeployment plan:", error);
    throw error;
  }
};

// PUT counterpart to addTalentMobilityRedeploymentPlan — replaces an
// already-saved plan's detail rows in place (same payload shape) instead of
// creating a new plan_id, keyed by the plan_id a prior create/update
// returned (see useRedeploymentPlanActions).
export const updateTalentMobilityRedeploymentPlan = async (planId, payload) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.TALENT_MOBILITY_REDEPLOYMENT_PLAN}/${planId}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating Talent Mobility redeployment plan:", error);
    throw error;
  }
};

// POST — creates a PAIC Org Redesign plan: one talent_mobility_plans row
// (plan_type "paic_org_redesign") plus one tm_paic_redesign_plan row per
// version (sb-monarch-backend paic_redesign_plan_service). Payload, built by
// usePaicRedesignPlanStore's preparePlanSave:
// { plan_name, organizations: [{ sb_org_redesign_unique_id, roles: [{
// role_key, change_type ("REDUCED" | "REDUNDANT" | "INCREASED" | "CREATED"),
// target_position, available_employee_targets: [same entries as the
// redeployment plan] }] }], client_request_id }. client_request_id is the
// draft's idempotency key (usePaicRedesignPlanActions): a retry with the same
// key returns the plan already stored instead of creating a duplicate. The
// backend checks every version is published and visible to the user and
// every role exists in it, and takes each role's JD and change_type from the
// version. Returns { plan_id, plan_name, plan_type, organizations: [{
// sb_org_redesign_unique_id }] }.
export const addTalentMobilityPaicRedesignPlan = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_PAIC_REDESIGN_PLAN, payload);
    return response.data;
  } catch (error) {
    logPaicApiError("Error submitting PAIC redesign plan:", error);
    throw error;
  }
};

// PUT counterpart to addTalentMobilityPaicRedesignPlan — syncs the saved
// plan's versions to the same payload shape (adds, updates and removes
// versions) and renames it, keyed by the plan_id a prior create returned
// (see usePaicRedesignPlanActions). 409 while another save of the same plan
// is in progress.
export const updateTalentMobilityPaicRedesignPlan = async (planId, payload) => {
  try {
    const response = await Api.put(`${API_ENDPOINTS.TALENT_MOBILITY_PAIC_REDESIGN_PLAN}/${planId}`, payload);
    return response.data;
  } catch (error) {
    logPaicApiError("Error updating PAIC redesign plan:", error);
    throw error;
  }
};

// GET ?plan_id= — one saved PAIC redesign plan's versions with their stored
// roles, in the save payload's role shape: { plan_id, organizations: [{
// sb_org_redesign_unique_id, roles: [{ role_key, jd_sb_unique_id,
// change_type, target_position, available_employee_targets }] }] }. Powers
// View Planning's Edit action, which loads it into usePaicRedesignPlanStore
// (see loadPlan) on the PAIC Org Redesign page to keep editing the same plan.
export const getPaicRedesignPlanDetails = async (planId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_PAIC_REDESIGN_PLAN_DETAILS, {
      params: { plan_id: planId },
    });
    return response.data;
  } catch (error) {
    logPaicApiError("Error fetching PAIC redesign plan details:", error);
    throw error;
  }
};

// One saved plan's stored redeployment_user_plan rows, read back in the same
// per-role shape (jd_sb_unique_id, available_employee_targets, target_position)
// the create/update payload used. Returns { plan_id, redeployment_plans: [...] }.
// Powers View Planning's Edit action, which loads this straight into
// useRedeploymentPlanStore (see loadPlan) before navigating into the
// Redeployment Center to keep editing the same plan.
export const getRedeploymentPlanDetails = async (planId) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_REDEPLOYMENT_PLAN_DETAILS, { plan_id: planId });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility redeployment plan details:", error);
    throw error;
  }
};

// Every PUBLISHED JD as { sb_unique_id, title, locations } — fills the
// aspiration form's Target Role dropdown on page load.
export const getTalentMobilityPublishedJobs = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_PUBLISHED_JOBS);
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility published jobs:", error);
    throw error;
  }
};

// Payload: { jd_sb_unique_id } — the open position applied to; the
// applicant is resolved server-side from the JWT. Idempotent: re-applying
// returns the existing application ("Already applied to this job.").
// Returns { message, sb_application_id, user_id, jd_sb_unique_id, status }.
export const applyTalentMobilityJob = async (payload) => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_APPLY, payload);
    return response.data;
  } catch (error) {
    console.error("Error applying to Talent Mobility job:", error);
    throw error;
  }
};

// Applications submitted via POST /apply, newest first — applicant
// name/email, the applied JD's title, and the applicant's SMI against it
// (people_profiles.profile_score; null when no profile row exists yet)
// merged in. Pass a jd_sb_unique_id to scope the list to one role (the
// Role Detail page's Applications view); omit it for every application
// org-wide. Returns { applications: [{ sb_application_id, user_id, name,
// email, jd_sb_unique_id, job_title, status, smi, applied_at }] }.
export const getTalentMobilityApplications = async (jdSbUniqueId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_APPLICATIONS, {
      params: jdSbUniqueId ? { jd_sb_unique_id: jdSbUniqueId } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility applications:", error);
    throw error;
  }
};

// Payload: { target_role_name, business_unit?, function?, sub_function?,
// locations?, relocate? } — the aspiration form's "Save & Continue". The
// backend resolves the target role(s) by NAME (POST /career-paths has no
// id-based mode — target_role_id isn't read by that route at all, see
// talent_mobility.career_paths' own docstring), narrowed by whichever of
// business_unit/function/sub_function/locations are provided; relocate
// defaults true. The employee's current role is NOT sent — the backend
// resolves it from the caller's JWT. Returns
// { sb_pathway_unique_id, current_jd_sb_unique_id, tracks }.
export const createTalentMobilityCareerPaths = async (payload) => {
  // An empty payload is the OTHER call on this endpoint — "return my saved
  // aspiration's pathway" (fetchSavedTalentMobilityCareerPaths below); the
  // backend picks that mode whenever target_role_name is missing/blank. A
  // create that lost its target_role_name must fail loudly here rather than
  // silently degrading into that rehydration mode and fetching the
  // previously saved pathway for the wrong target.
  if (!payload?.target_role_name || !String(payload.target_role_name).trim()) {
    throw new Error("createTalentMobilityCareerPaths requires payload.target_role_name");
  }
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_CAREER_PATHS, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating Talent Mobility career paths:", error);
    throw error;
  }
};

// Empty-payload variant of POST /career-paths — page-load rehydration for
// /talent-mobility-employee (the aspiration store is session-only, so a
// reload starts empty). Without a target_role_id the backend looks up the
// caller's saved aspiration (talent_mobility_aspiration) and returns its
// stored pathway plus { target_role_id, target_role_title, locations };
// 404 means no aspiration was ever saved — returned as null so the page
// shows the capture form instead of treating it as an error.
export const fetchSavedTalentMobilityCareerPaths = async () => {
  try {
    const response = await Api.post(API_ENDPOINTS.TALENT_MOBILITY_CAREER_PATHS, {});
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    console.error("Error fetching saved Talent Mobility career paths:", error);
    throw error;
  }
};

// The position-card refresh button — checks the caller's stored SMI for one
// career-paths position (jd_sb_unique_id) and, when it's missing, reports or
// (re)starts the batch inference for that (user, JD) pair. Returns
// { jd_sb_unique_id, smi_score, is_smi_loading,
//   status: "ready"|"started"|"in_progress"|"restarted", message }.
export const getTalentMobilityCareerPathsSmiStatus = async (jdSbUniqueId) => {
  try {
    const response = await Api.get(API_ENDPOINTS.TALENT_MOBILITY_CAREER_PATHS_SMI_STATUS, {
      params: { jd_sb_unique_id: jdSbUniqueId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Talent Mobility career paths SMI status:", error);
    throw error;
  }
};

// Payload: { sb_unique_id } plus only the field that changed —
// job_opening_status ("open"/"close") or is_job_discoverable (boolean).
export const updateTalentMobilityJobOpeningStatus = async (payload) => {
  try {
    const response = await Api.put(API_ENDPOINTS.TALENT_MOBILITY_JOB_OPENING_STATUS, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating Talent Mobility job opening status:", error);
    throw error;
  }
};

// ─── Succession Planning — Org Tree ───────────────────────────────────────────

export const getSuccessionTreeFilters = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.SUCCESSION_TREE_FILTERS);
    return response.data;
  } catch (error) {
    console.error("Error fetching succession tree filter options:", error);
    throw error;
  }
};

// params: { legalEntity, businessUnit, function, subFunction, level } — each an
// array of selected values, comma-joined into one query param when non-empty
// (empty/omitted arrays are never sent). rootEmpId/includeInactive/maxDepth
// pass straight through when set.
export const getSuccessionTree = async (params = {}) => {
  try {
    const queryParts = [];
    const pushMultiValue = (key, values) => {
      if (values?.length) queryParts.push(`${key}=${values.map(encodeURIComponent).join(",")}`);
    };
    pushMultiValue("legal_entity", params.legalEntity);
    pushMultiValue("business_unit", params.businessUnit);
    pushMultiValue("function", params.function);
    pushMultiValue("sub_function", params.subFunction);
    pushMultiValue("level", params.level);
    if (params.rootEmpId) queryParts.push(`root_emp_id=${encodeURIComponent(params.rootEmpId)}`);
    if (params.includeInactive != null) queryParts.push(`include_inactive=${params.includeInactive}`);
    if (params.maxDepth != null) queryParts.push(`max_depth=${params.maxDepth}`);
    const url = queryParts.length
      ? `${API_ENDPOINTS.SUCCESSION_TREE}?${queryParts.join("&")}`
      : API_ENDPOINTS.SUCCESSION_TREE;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching succession tree:", error);
    throw error;
  }
};

// --- WORK TRANSFORMATION DASHBOARD APIs START ---

export const getWorkTransformationSummary = async () => {
  try {
    const response = await Api.get(API_ENDPOINTS.WORK_TRANSFORMATION_SUMMARY);
    return response.data;
  } catch (error) {
    console.error("Error fetching work transformation summary:", error);
    throw error;
  }
};

export const getWorkTransformationTotalPotential = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.metric) queryParts.push(`metric=${encodeURIComponent(params.metric)}`);
    if (params.segment) queryParts.push(`segment=${encodeURIComponent(params.segment)}`);
    if (params.value) queryParts.push(`value=${encodeURIComponent(params.value)}`);
    const url = queryParts.length
      ? `${API_ENDPOINTS.WORK_TRANSFORMATION_TOTAL_POTENTIAL}?${queryParts.join("&")}`
      : API_ENDPOINTS.WORK_TRANSFORMATION_TOTAL_POTENTIAL;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching work transformation total potential:", error);
    throw error;
  }
};

export const getWorkTransformationCollaboration = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.segment) queryParts.push(`segment=${encodeURIComponent(params.segment)}`);
    if (params.value) queryParts.push(`value=${encodeURIComponent(params.value)}`);
    const url = queryParts.length
      ? `${API_ENDPOINTS.WORK_TRANSFORMATION_COLLABORATION}?${queryParts.join("&")}`
      : API_ENDPOINTS.WORK_TRANSFORMATION_COLLABORATION;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching work transformation collaboration:", error);
    throw error;
  }
};

export const getWorkTransformationRoiLeaderboard = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.limit) queryParts.push(`limit=${encodeURIComponent(params.limit)}`);
    if (params.segment) queryParts.push(`segment=${encodeURIComponent(params.segment)}`);
    if (params.value) queryParts.push(`value=${encodeURIComponent(params.value)}`);
    const url = queryParts.length
      ? `${API_ENDPOINTS.WORK_TRANSFORMATION_ROI_LEADERBOARD}?${queryParts.join("&")}`
      : API_ENDPOINTS.WORK_TRANSFORMATION_ROI_LEADERBOARD;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching work transformation ROI leaderboard:", error);
    throw error;
  }
};

export const getWorkTransformationSkills = async (params = {}) => {
  try {
    const queryParts = [];
    if (params.segment) queryParts.push(`segment=${encodeURIComponent(params.segment)}`);
    if (params.value) queryParts.push(`value=${encodeURIComponent(params.value)}`);
    const url = queryParts.length
      ? `${API_ENDPOINTS.WORK_TRANSFORMATION_SKILLS}?${queryParts.join("&")}`
      : API_ENDPOINTS.WORK_TRANSFORMATION_SKILLS;
    const response = await Api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching work transformation skills:", error);
    throw error;
  }
};

// --- WORK TRANSFORMATION DASHBOARD APIs END ---
